/**
 * AdminCost — дашборд по стоимости LLM-вызовов.
 *
 * Источник данных: /api/ai/agent/cost (overall + by_role + by_model) +
 * /api/ai/agent/cost/timeseries (per-hour buckets).
 *
 * Без авторизации (страница пока внутренняя). Если нужно — обернуть
 * проверкой is_admin.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from "recharts";
import { API_HOST } from "@/constants";

interface CostSummary {
  overall: {
    n_events: number;
    total_cost_usd: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_cached_tokens: number;
    cache_hit_rate?: number;
    first_ts?: number;
    last_ts?: number;
  };
  by_role: Record<string, {
    n_events: number;
    total_cost_usd: number;
    avg_cost_usd: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_cached_tokens: number;
    cache_hit_rate?: number;
    models: Record<string, number>;
  }>;
  by_model: Record<string, {
    n_events: number;
    total_cost_usd: number;
    avg_cost_usd: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
  }>;
  source?: string;
}

interface TimeseriesBucket {
  ts: number;
  n_events: number;
  cost_usd: number;
  prompt_tokens: number;
  completion_tokens: number;
  cached_tokens: number;
}

interface TimeseriesResp {
  buckets: TimeseriesBucket[];
  bucket_sec: number;
  role_filter?: string | null;
}

const formatTs = (ts: number) => {
  const d = new Date(ts * 1000);
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, "0")}:00`;
};

const formatUsd = (v: number) =>
  v < 0.001 ? `$${(v * 1000).toFixed(2)}m` : `$${v.toFixed(4)}`;

export default function AdminCost() {
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sinceHours, setSinceHours] = useState<number>(168);  // 7 дней default
  const [bucketSec, setBucketSec] = useState<number>(3600);   // 1 час
  const [roleFilter, setRoleFilter] = useState<string>("");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, tsRes] = await Promise.all([
        fetch(`${API_HOST}/api/ai/agent/cost?source=jsonl&since_hours=${sinceHours}`),
        fetch(
          `${API_HOST}/api/ai/agent/cost/timeseries?bucket_sec=${bucketSec}&since_hours=${sinceHours}` +
          (roleFilter ? `&role=${encodeURIComponent(roleFilter)}` : ""),
        ),
      ]);
      if (!sumRes.ok) throw new Error(`summary ${sumRes.status}`);
      if (!tsRes.ok) throw new Error(`timeseries ${tsRes.status}`);
      setSummary(await sumRes.json());
      setTimeseries(await tsRes.json());
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinceHours, bucketSec, roleFilter]);

  const roleData = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.by_role)
      .map(([role, data]) => ({
        role,
        cost: Number(data.total_cost_usd.toFixed(6)),
        events: data.n_events,
        avg: Number(data.avg_cost_usd.toFixed(6)),
        cache: data.cache_hit_rate ?? 0,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [summary]);

  const modelData = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.by_model)
      .map(([model, data]) => ({
        model: model.length > 28 ? model.slice(0, 28) + "…" : model,
        cost: Number(data.total_cost_usd.toFixed(6)),
        events: data.n_events,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [summary]);

  const tsData = useMemo(() => {
    if (!timeseries) return [];
    return timeseries.buckets.map((b) => ({
      label: formatTs(b.ts),
      cost: Number(b.cost_usd.toFixed(6)),
      events: b.n_events,
      prompt: b.prompt_tokens,
      completion: b.completion_tokens,
    }));
  }, [timeseries]);

  const overall = summary?.overall;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="mx-auto max-w-7xl p-6">
        <header className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">AI Cost Dashboard</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Стоимость LLM-вызовов. Источник: <code>var/cost_events.jsonl</code>.
              Per-process /cost endpoint объединяет current + .1 backup.
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm"
          >
            {loading ? "Обновляю…" : "Обновить"}
          </button>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Окно:</span>
            <select
              value={sinceHours}
              onChange={(e) => setSinceHours(Number(e.target.value))}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1"
            >
              <option value={1}>1 час</option>
              <option value={24}>24 часа</option>
              <option value={168}>7 дней</option>
              <option value={720}>30 дней</option>
              <option value={0}>Всё время</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Бакет:</span>
            <select
              value={bucketSec}
              onChange={(e) => setBucketSec(Number(e.target.value))}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1"
            >
              <option value={300}>5 мин</option>
              <option value={3600}>1 час</option>
              <option value={86400}>1 день</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Role:</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1"
            >
              <option value="">все</option>
              {summary && Object.keys(summary.by_role).map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-sm">
            ⚠️ {error}
          </div>
        )}

        {/* Summary cards */}
        {overall && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryCard
              label="Total cost"
              value={`$${overall.total_cost_usd.toFixed(4)}`}
            />
            <SummaryCard label="Events" value={overall.n_events.toString()} />
            <SummaryCard
              label="Avg cost / event"
              value={
                overall.n_events
                  ? formatUsd(overall.total_cost_usd / overall.n_events)
                  : "—"
              }
            />
            <SummaryCard
              label="Cache hit rate"
              value={
                overall.cache_hit_rate != null
                  ? `${(overall.cache_hit_rate * 100).toFixed(1)}%`
                  : "—"
              }
            />
            <SummaryCard
              label="Prompt tokens"
              value={overall.total_prompt_tokens.toLocaleString()}
            />
            <SummaryCard
              label="Completion tokens"
              value={overall.total_completion_tokens.toLocaleString()}
            />
            <SummaryCard
              label="Cached tokens"
              value={overall.total_cached_tokens.toLocaleString()}
            />
            <SummaryCard
              label="Range"
              value={
                overall.first_ts && overall.last_ts
                  ? `${formatTs(overall.first_ts)} → ${formatTs(overall.last_ts)}`
                  : "—"
              }
              small
            />
          </div>
        )}

        {/* Cost time-series */}
        {tsData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <h2 className="text-sm font-semibold mb-3">
              Cost per bucket ({timeseries?.bucket_sec === 86400 ? "день" : timeseries?.bucket_sec === 300 ? "5 мин" : "час"})
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(4)}`} />
                  <Tooltip
                    formatter={((v: number) => `$${v.toFixed(6)}`) as any}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="cost" stroke="#2563eb" name="Cost USD" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* By role bar chart */}
        {roleData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <h2 className="text-sm font-semibold mb-3">Cost by role</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roleData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(4)}`} />
                  <YAxis type="category" dataKey="role" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={((v: number) => `$${v.toFixed(6)}`) as any} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="cost" fill="#2563eb" name="Cost USD" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <table className="w-full text-xs mt-3">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-1">Role</th>
                  <th className="text-right py-1">Events</th>
                  <th className="text-right py-1">Total</th>
                  <th className="text-right py-1">Avg</th>
                  <th className="text-right py-1">Cache hit</th>
                </tr>
              </thead>
              <tbody>
                {roleData.map((r) => (
                  <tr key={r.role} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-1 font-mono">{r.role}</td>
                    <td className="text-right py-1">{r.events}</td>
                    <td className="text-right py-1">${r.cost.toFixed(4)}</td>
                    <td className="text-right py-1">{formatUsd(r.avg)}</td>
                    <td className="text-right py-1">
                      {(r.cache * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* By model */}
        {modelData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-sm font-semibold mb-3">Cost by model</h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={modelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(4)}`} />
                  <YAxis type="category" dataKey="model" width={200} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={((v: number) => `$${v.toFixed(6)}`) as any} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="cost" fill="#10b981" name="Cost USD" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!loading && !overall?.n_events && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
            Нет данных за выбранное окно. Сделай несколько запросов к agent или AI Tutor — события появятся.
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label, value, small = false,
}: {
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={small ? "text-sm mt-1" : "text-lg font-semibold mt-1"}>{value}</div>
    </div>
  );
}
